import { useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { Alert, Button, Card, Field, Input, PageHeader } from "@empite/ui-components";
import { isApiError } from "@empite/api-client";
import type {
  ApplicationEdit,
  ApplicationSummary,
  NewApplication,
  NewOAuthClient,
  OAuthClient,
  OAuthClientEdit,
  Reason,
} from "../admin-client";
import {
  useCreateApplication,
  useCreateOAuthClient,
  useReplaceRedirectURIs,
  useUpdateApplication,
  useUpdateOAuthClient,
} from "../admin-hooks";
import { ErrorAlert, RequireOperator, can, errorMessage } from "./guards";
import { ReasonButton } from "./reason-prompt";
import { NotPermitted } from "./packages";
import { MAX_REDIRECT_URIS, redirectURIIssues, redirectURIsForSubmit } from "./redirect-uris";
import type { RedirectURIIssues } from "./redirect-uris";

/**
 * The application and OAuth-client forms.
 *
 * One file rather than a copy on each screen, for the reason models.tsx gives:
 * the fields are the same fields whichever route they are going to, and two
 * copies drift the first time a column is added — the create half grows it and
 * the edit half quietly stops round tripping it, which reads to an operator as
 * the console clearing a value they never touched.
 *
 * Both edits PATCH the WHOLE row back, the immutable identity included. That
 * is the shape the API was built for: echoing `slug` or `client_id` unchanged
 * is a no-op and MOVING one is a 409 whose sentence names which was aimed at,
 * while a body that omitted them could not tell the two apart. It also means a
 * save that changes nothing returns the row rather than the "this edit changes
 * nothing" 400 an empty PATCH earns.
 *
 * The CREATE is a page and the EDIT is a subform, and that asymmetry is
 * deliberate. Registering an application is the start of a job with several
 * more steps (a client, then a routing profile, then enabling it), so it gets a
 * URL an operator can be sent to and come back to. Correcting a name is one
 * field on a page they are already reading.
 */

/** The permission the Admin API guards every write here with; repeated only to
 *  decide what to draw. The API re-checks it and audits the refusal. */
const WRITE = "applications:write";

// --------------------------------------------------------------------------
// Scopes
// --------------------------------------------------------------------------

/**
 * The scope vocabulary this platform actually understands.
 *
 * Mirrored from `internal/auth/seed.go` (`appAScopes`) and the table in
 * `docs/desktop-client-integration.md`. It is repeated here for the same
 * reason models.tsx repeats the provider registry: to decide what to DRAW.
 *
 * There is no closed vocabulary in the database — `allowed_scopes` is
 * `text[]`, and the server validates the SHAPE of each entry (non-empty, no
 * whitespace, bounded, no duplicates) and nothing about its membership. That is
 * on purpose: a partner shipping a client is not blocked on a migration. But
 * free text is the wrong control HERE, because a scope is held against itself
 * at authorize time and nothing ever refuses a typo: `inference:invote` is
 * registered happily, granted happily, and simply never matches, so the client
 * signs a user in and then holds nothing that works. A misspelling is
 * indistinguishable from a scope nobody has implemented yet, and the failure
 * surfaces days later as "sign-in works but the app does nothing".
 *
 * So the picker is the vocabulary, and a scope a client already holds that is
 * NOT in it is still shown and still checked — see scopeOptions. Dropping one
 * silently would be this console editing config it does not understand.
 */
const PLATFORM_SCOPES: ReadonlyArray<{ scope: string; label: string }> = [
  { scope: "profile:read", label: "Read the signed-in customer's profile" },
  { scope: "wallet:read", label: "Read the customer's wallet and balance" },
  { scope: "inference:invoke", label: "Send inference requests through the gateway" },
];

interface ScopeOption {
  scope: string;
  label: string;
  /** False for a scope this build has never heard of but the client holds. */
  known: boolean;
}

/** The vocabulary, plus anything this client already holds that is not in it —
 *  in that order, so the familiar list does not move about. */
function scopeOptions(held: string[]): ScopeOption[] {
  const vocabulary = PLATFORM_SCOPES.map((entry) => ({ ...entry, known: true }));
  const names = new Set(PLATFORM_SCOPES.map((entry) => entry.scope));
  const extras = held
    .filter((scope) => !names.has(scope))
    .map((scope) => ({
      scope,
      label: "Not in this build's vocabulary — registered against this client",
      known: false,
    }));
  return [...vocabulary, ...extras];
}

function ScopePicker({
  idPrefix,
  options,
  chosen,
  onToggle,
  error,
}: {
  idPrefix: string;
  options: ScopeOption[];
  chosen: string[];
  onToggle: (scope: string, on: boolean) => void;
  error?: string;
}) {
  // A fieldset rather than a Field: Field wires ONE label to ONE control by
  // id, and a group of checkboxes has no single control for a label to point
  // at. The classes are Field's so it still looks like every other row.
  return (
    <fieldset className={`esm-field${error ? " esm-field--invalid" : ""}`}>
      <legend className="esm-field__label">Allowed scopes</legend>
      <ul className="esm-check-list">
        {options.map((option) => (
          <li key={option.scope}>
            <input
              id={`${idPrefix}-scope-${option.scope}`}
              type="checkbox"
              checked={chosen.includes(option.scope)}
              onChange={(event) => onToggle(option.scope, event.target.checked)}
            />
            <label htmlFor={`${idPrefix}-scope-${option.scope}`}>
              <code>{option.scope}</code>
              <span className={option.known ? "esm-muted-text" : "esm-check-list__unknown"}>
                {option.label}
              </span>
            </label>
          </li>
        ))}
      </ul>
      {error ? (
        <p className="esm-field__error" role="alert">
          {error}
        </p>
      ) : (
        <p className="esm-field__hint">
          The ceiling on what this client may even ask a customer for. An <code>/authorize</code>{" "}
          call naming no scope is granted the whole list, so it is not a default — it is the
          maximum.
        </p>
      )}
    </fieldset>
  );
}

// --------------------------------------------------------------------------
// Redirect URIs
// --------------------------------------------------------------------------

/**
 * A client's callback list as an editable list of rows.
 *
 * Exported because the create form below and the replacement editor on the
 * application page are the same list under the same rules, and a second copy
 * would be a second answer to "what may be registered".
 *
 * A row's error is passed IN rather than computed here, so the caller can put
 * the API's own sentence against the row the API named without this component
 * knowing anything about problem documents.
 */
export function RedirectURIRows({
  idPrefix,
  uris,
  onChange,
  errors,
  listError,
  disabled = false,
}: {
  idPrefix: string;
  uris: string[];
  onChange: (next: string[]) => void;
  errors: Array<string | undefined>;
  listError?: string;
  disabled?: boolean;
}) {
  return (
    <>
      {uris.map((uri, index) => (
        // Keyed by POSITION, which is normally the wrong key and is the right
        // one here: the rows have no identity of their own, two rows may hold
        // the same text while a duplicate is being fixed, and deleting row 2
        // must leave the box that was row 3 holding row 3's text.
        <Field
          key={`${idPrefix}-uri-${index}`}
          label={`Redirect uri ${index + 1}`}
          htmlFor={`${idPrefix}-uri-${index}`}
          error={errors[index]}
        >
          <div className="esm-row-with-action">
            <Input
              id={`${idPrefix}-uri-${index}`}
              value={uri}
              spellCheck={false}
              disabled={disabled}
              placeholder="https://app.example.com/callback"
              onChange={(event) => {
                const next = [...uris];
                next[index] = event.target.value;
                onChange(next);
              }}
            />
            <Button
              variant="ghost"
              disabled={disabled}
              aria-label={`Remove redirect uri ${index + 1}`}
              onClick={() => onChange(uris.filter((_, at) => at !== index))}
            >
              Remove
            </Button>
          </div>
        </Field>
      ))}
      {listError ? (
        <p className="esm-field__error" role="alert">
          {listError}
        </p>
      ) : null}
      <div className="esm-actions">
        <Button
          variant="secondary"
          disabled={disabled || uris.length >= MAX_REDIRECT_URIS}
          onClick={() => onChange([...uris, ""])}
        >
          Add a redirect uri
        </Button>
      </div>
    </>
  );
}

// --------------------------------------------------------------------------
// Which field a refusal is about
// --------------------------------------------------------------------------

/**
 * The RFC 9457 extension members of a refusal, if it is one of ours.
 *
 * The cast is because `Problem` carries the members RFC 9457 declares and
 * extensions are, by definition, not among them.
 */
function problemMembers(error: unknown): Record<string, unknown> | undefined {
  if (!isApiError(error) || !error.problem) {
    return undefined;
  }
  return error.problem as unknown as Record<string, unknown>;
}

/** The redirect uri a refusal names, if it named one. Both the 400 for a
 *  malformed or duplicated entry and the 409 for a callback another client
 *  owns carry it, which is what lets either be shown against a row. */
export function offendingRedirectURI(error: unknown): string | undefined {
  const uri = problemMembers(error)?.["redirect_uri"];
  return typeof uri === "string" ? uri : undefined;
}

type IdentityField = "slug" | "client_id";

/**
 * Which identity field a refusal is about, so the sentence lands on the box the
 * operator has to change rather than in a banner above the whole form.
 *
 * Two different 409s end up here and they are told apart by their MEMBERS,
 * never by their sentences:
 *
 *   * the immutability refusal carries `field`, and its value is already the
 *     name of the box ("slug" or "client_id");
 *   * a COLLISION carries nothing, because there is only one thing it can be
 *     about — an application is unique on its slug and a client on its
 *     client_id, and both are `already_exists`. A collision that named a
 *     redirect uri is a different rule and is excluded here so it can be shown
 *     against the row that holds it.
 */
export function offendingIdentity(
  error: unknown,
  identity: IdentityField,
): string | undefined {
  if (!isApiError(error)) {
    return undefined;
  }
  const members = problemMembers(error);
  const field = members?.["field"];
  if (typeof field === "string") {
    return field === identity ? error.message : undefined;
  }
  if (error.code === "already_exists" && offendingRedirectURI(error) === undefined) {
    return error.message;
  }
  return undefined;
}


// --------------------------------------------------------------------------
// The application form
// --------------------------------------------------------------------------

interface ApplicationFormState {
  slug: string;
  name: string;
  description: string;
}

/** The server's own rules, restated where they can be heard before the write.
 *  `applicationSlugPattern`, character for character. */
const SLUG_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;
const MAX_SLUG = 64;
const MAX_NAME = 200;
const MAX_DESCRIPTION = 2000;

function slugError(value: string, creating: boolean): string | undefined {
  if (!creating) {
    return undefined;
  }
  const slug = value.trim();
  if (slug === "") {
    return "Required — and it cannot be changed afterwards.";
  }
  if (slug.length > MAX_SLUG) {
    return `Longer than ${MAX_SLUG} characters.`;
  }
  return SLUG_PATTERN.test(slug)
    ? undefined
    : "Lowercase letters, digits and single hyphens between them, as in studio-desktop.";
}

function nameError(value: string, noun: string): string | undefined {
  const name = value.trim();
  if (name === "") {
    return `${noun} needs a name.`;
  }
  return name.length > MAX_NAME ? `Longer than ${MAX_NAME} characters.` : undefined;
}

function ApplicationFields({
  idPrefix,
  form,
  setForm,
  creating,
  slugConflict,
}: {
  idPrefix: string;
  form: ApplicationFormState;
  setForm: (next: ApplicationFormState) => void;
  creating: boolean;
  slugConflict?: string;
}) {
  const set = (field: keyof ApplicationFormState) => (value: string) =>
    setForm({ ...form, [field]: value });

  return (
    <>
      <div className="esm-form-grid">
        <Field
          label="Slug"
          htmlFor={`${idPrefix}-slug`}
          hint={
            creating
              ? "The lowercase handle the seed, the smoke gate and every runbook name this product by. Unique across the whole platform, and immutable once written."
              : "Immutable. It is what `make seed` upserts on and what a runbook names, so it is not something an edit may move."
          }
          // The form's own rule while typing; the API's own sentence once it
          // has refused. Never both, and never a house-worded paraphrase of
          // either.
          error={slugConflict ?? slugError(form.slug, creating)}
        >
          <Input
            id={`${idPrefix}-slug`}
            value={form.slug}
            spellCheck={false}
            disabled={!creating}
            onChange={(event) => set("slug")(event.target.value)}
          />
        </Field>
        <Field
          label="Name"
          htmlFor={`${idPrefix}-name`}
          hint="What customers see."
          error={nameError(form.name, "An application")}
        >
          <Input
            id={`${idPrefix}-name`}
            value={form.name}
            onChange={(event) => set("name")(event.target.value)}
          />
        </Field>
      </div>
      <Field
        label="Description"
        htmlFor={`${idPrefix}-description`}
        hint={`An operator-facing note, never shown to customers. Up to ${MAX_DESCRIPTION} characters.`}
        error={
          form.description.trim().length > MAX_DESCRIPTION
            ? `Longer than ${MAX_DESCRIPTION} characters.`
            : undefined
        }
      >
        <textarea
          id={`${idPrefix}-description`}
          className="esm-input"
          rows={3}
          value={form.description}
          onChange={(event) => set("description")(event.target.value)}
        />
      </Field>
    </>
  );
}

function applicationFormReady(form: ApplicationFormState, creating: boolean): boolean {
  return (
    slugError(form.slug, creating) === undefined &&
    nameError(form.name, "An application") === undefined &&
    form.description.trim().length <= MAX_DESCRIPTION
  );
}

// --------------------------------------------------------------------------
// Create: /applications/new
// --------------------------------------------------------------------------

export function ApplicationCreate() {
  return (
    <RequireOperator>
      {(session) =>
        can(session.operator, WRITE) ? (
          <ApplicationCreateForm />
        ) : (
          <NotPermitted title="New application" permission={WRITE} />
        )
      }
    </RequireOperator>
  );
}

function ApplicationCreateForm() {
  const navigate = useNavigate();
  const create = useCreateApplication();
  const [form, setForm] = useState<ApplicationFormState>({
    slug: "",
    name: "",
    description: "",
  });

  const slugConflict = offendingIdentity(create.error, "slug");

  async function run(reason: Reason) {
    const description = form.description.trim();
    const body: NewApplication = {
      slug: form.slug.trim(),
      name: form.name.trim(),
      // Omitted rather than sent empty: `null` on this column means nobody has
      // ever written a note and `""` means somebody cleared one, and a create
      // that says nothing about it should leave it in the first state.
      description: description === "" ? undefined : description,
      // suite_id is deliberately not a field. A tenant has one suite and the
      // server resolves it from the signed-in operator's own tenant; platform
      // staff have no tenant, so the server refuses rather than guesses and
      // answers with a sentence saying so, which the banner shows verbatim. A
      // picker over a list this console does not otherwise read would be a box
      // a tenant operator answers the same way every time.
    };
    const result = await create.mutateAsync({ body, reason });
    await navigate({
      to: "/applications/$applicationId",
      params: { applicationId: result.application.id },
    });
  }

  return (
    <>
      <PageHeader
        title="Register an application"
        description={
          <>
            An application is a product in the suite: the thing a customer is entitled to, an
            OAuth client authorises against, and a routing profile prices. ·{" "}
            <Link to="/applications">back to all applications</Link>
          </>
        }
      />
      <Card title="The application">
        <p className="esm-muted-text">
          It is created <strong>disabled</strong>, against the column&rsquo;s own default. It has
          no OAuth client yet, so nothing can authorise against it — enable it from its page once
          the client and the routing profile are in place.
        </p>
        {/* The API's own refusal, verbatim, unless it is already sitting
            against the slug box. A duplicate slug, an unresolvable suite and
            an over-long note are three different problems and the operator has
            to be told which one they hit. */}
        {slugConflict === undefined ? (
          <ErrorAlert error={create.error} title="That application was not registered" />
        ) : null}
        {/* Not a <form>: the write goes through the reason prompt rather than a
            submit, and an Enter key that posted straight past the prompt would
            be a mutation with no reason on it. */}
        <ApplicationFields
          idPrefix="new-application"
          form={form}
          setForm={setForm}
          creating
          slugConflict={slugConflict}
        />
        <div className="esm-actions">
          <ReasonButton
            variant="primary"
            busy={create.isPending}
            disabled={!applicationFormReady(form, true)}
            mutation={{
              title: `Register ${form.name.trim() || "an application"}`,
              tone: "primary",
              description: (
                <p>
                  A disabled application under this deployment&rsquo;s suite, handled by{" "}
                  <code>{form.slug.trim() || "its slug"}</code>. The slug is immutable from this
                  moment: it is what the seed upserts on and what a runbook names.
                </p>
              ),
              confirmLabel: "Register application",
              run,
            }}
          >
            Register application
          </ReasonButton>
          <Link className="esm-button esm-button--ghost" to="/applications">
            Cancel
          </Link>
        </div>
      </Card>
    </>
  );
}

// --------------------------------------------------------------------------
// Edit: the subform on the application page
// --------------------------------------------------------------------------

export function ApplicationEditForm({
  app,
  onClose,
  onDone,
}: {
  app: ApplicationSummary;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const update = useUpdateApplication();
  const slugConflict = offendingIdentity(update.error, "slug");
  const [form, setForm] = useState<ApplicationFormState>(() => ({
    slug: app.slug,
    name: app.name,
    // `null` is "nobody ever wrote one" and `""` is "somebody cleared it".
    // Both are an empty box; what keeps them apart is what the save sends.
    description: app.description ?? "",
  }));

  async function run(reason: Reason) {
    const description = form.description.trim();
    const body: ApplicationEdit = {
      // Echoed from the ROW, never from the input: the box is disabled, so
      // there is nothing here that could ask to move it. Sent all the same,
      // because a whole-row PATCH is the shape the API was built for and it
      // makes a save that changes nothing a 200 rather than the empty-PATCH
      // 400.
      slug: app.slug,
      name: form.name.trim(),
      // The one field where "leave it alone" and "clear it" are different
      // requests. An empty box over a description that was NEVER WRITTEN omits
      // the key — sending "" would move the column from null to the empty
      // string and quietly claim somebody cleared a note that never existed.
      // An empty box over a note that DOES exist sends "", which is the clear.
      description: app.description === null && description === "" ? undefined : description,
    };
    const result = await update.mutateAsync({ applicationID: app.id, body, reason });
    onDone(`Saved ${result.application.name}.`);
  }

  return (
    <div className="esm-subform">
      <h3>Edit {app.name}</h3>
      <p className="esm-muted-text">
        The slug and the suite are fixed. Everything else here is a correction to what was
        recorded; withdrawing the application is the status button above.
      </p>
      {slugConflict === undefined ? (
        <ErrorAlert error={update.error} title="That change was not saved" />
      ) : null}
      <ApplicationFields
        idPrefix={`application-${app.id}`}
        form={form}
        setForm={setForm}
        creating={false}
        slugConflict={slugConflict}
      />
      <div className="esm-actions">
        <ReasonButton
          variant="primary"
          busy={update.isPending}
          disabled={!applicationFormReady(form, false)}
          mutation={{
            title: `Save changes to ${app.name}`,
            tone: "primary",
            description: (
              <p>
                The label and the operator-facing note on <code>{app.slug}</code>. Nothing about
                what this application may do changes.
              </p>
            ),
            confirmLabel: "Save changes",
            run,
          }}
        >
          Save changes
        </ReasonButton>
        <Button variant="ghost" disabled={update.isPending} onClick={onClose}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------
// The OAuth client form
// --------------------------------------------------------------------------

/** What the client form is currently for: a new client under this application,
 *  or one that exists. */
export type ClientFormTarget = { mode: "create" } | { mode: "edit"; client: OAuthClient };

interface ClientFormState {
  client_id: string;
  name: string;
  scopes: string[];
  /** Create only. Replacing an existing client's callbacks is its own route
   *  with its own audit action, because it is the one edit that can stop a
   *  shipped installation from finishing a sign-in. */
  redirect_uris: string[];
}

/** `oauthClientIDPattern`, character for character: letters, digits, dots,
 *  hyphens and underscores, starting with a letter or digit. Looser than a
 *  slug because a client id is conventionally reverse-DNS or vendor-prefixed
 *  and this rule must not refuse the ones a partner already ships. */
const CLIENT_ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._-]*$/;
const MAX_CLIENT_ID = 128;
const MAX_SCOPES = 32;

function clientIDError(value: string, creating: boolean): string | undefined {
  if (!creating) {
    return undefined;
  }
  const clientID = value.trim();
  if (clientID === "") {
    return "Required — the public identifier the desktop app sends on every /authorize call.";
  }
  if (clientID.length > MAX_CLIENT_ID) {
    return `Longer than ${MAX_CLIENT_ID} characters.`;
  }
  return CLIENT_ID_PATTERN.test(clientID)
    ? undefined
    : "Letters, digits, dots, hyphens and underscores, starting with a letter or digit. It travels in a query string and is compared byte for byte.";
}

function scopesError(scopes: string[]): string | undefined {
  if (scopes.length === 0) {
    return (
      "Choose at least one. An /authorize call naming no scope is granted the client's whole " +
      "allow-list, so a client with an empty one signs a user in and then holds nothing."
    );
  }
  return scopes.length > MAX_SCOPES ? `A client may hold at most ${MAX_SCOPES} scopes.` : undefined;
}

/**
 * The one form behind "Register a client" and every client's "Edit".
 *
 * The create half asks for callbacks and the edit half does not: replacing a
 * registered callback list is its own route, because it is the only edit on a
 * client that can lock a shipped fleet out of signing in and it wants an audit
 * row carrying the before and after lists rather than a line in a detail map
 * about a client whose name also changed. The server enforces that too — a
 * `redirect_uris` key on the client PATCH is refused by name.
 */
export function OAuthClientForm({
  applicationID,
  target,
  onClose,
  onDone,
}: {
  applicationID: string;
  target: ClientFormTarget;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const create = useCreateOAuthClient();
  const update = useUpdateOAuthClient();
  // Narrowed once, at the top, so nothing below has to re-test the union and
  // no branch can end up reaching for a client that is not there.
  const editing = target.mode === "edit" ? target.client : null;
  const creating = editing === null;
  const [form, setForm] = useState<ClientFormState>(() =>
    target.mode === "create"
      ? {
          client_id: "",
          name: "",
          // Nothing preselected. A default allow-list is an allow-list nobody
          // decided, and this one is the ceiling on what the client may ask a
          // customer for.
          scopes: [],
          redirect_uris: [""],
        }
      : {
          client_id: target.client.client_id,
          name: target.client.name,
          scopes: [...target.client.allowed_scopes],
          redirect_uris: [],
        },
  );

  const error = creating ? create.error : update.error;
  const options = scopeOptions(editing?.allowed_scopes ?? []);
  const issues: RedirectURIIssues = creating
    ? redirectURIIssues(form.redirect_uris)
    : { rows: [] };
  const clientIDConflict = offendingIdentity(error, "client_id");
  // The API's sentence goes against the ROW it named, in the API's own words.
  // Matched on the value the form SENT — trimmed the way the server trims it —
  // because that is the string the extension member quotes back.
  const refusedURI = offendingRedirectURI(error);
  const refusedRow =
    refusedURI === undefined
      ? -1
      : redirectURIsForSubmit(form.redirect_uris).indexOf(refusedURI);
  const rowErrors = creating
    ? form.redirect_uris.map((_uri, index) =>
        index === refusedRow ? errorMessage(error) : issues.rows[index],
      )
    : [];
  // The banner is silenced only once the sentence has actually LANDED
  // somewhere. A refusal naming a callback no row holds any more — the operator
  // deleted it, or the server trimmed differently — must not vanish because it
  // was assumed to be on a row.
  const placed = clientIDConflict !== undefined || refusedRow >= 0;

  const ready =
    clientIDError(form.client_id, creating) === undefined &&
    nameError(form.name, "An OAuth client") === undefined &&
    scopesError(form.scopes) === undefined &&
    (!creating || (issues.list === undefined && issues.rows.every((row) => row === undefined)));

  function toggleScope(scope: string, on: boolean) {
    setForm((current) => ({
      ...current,
      scopes: on
        ? [...current.scopes, scope]
        : current.scopes.filter((held) => held !== scope),
    }));
  }

  async function run(reason: Reason) {
    if (creating) {
      const body: NewOAuthClient = {
        client_id: form.client_id.trim(),
        name: form.name.trim(),
        // Ordered by the picker rather than by click order, so two clients
        // registered with the same scopes read identically in the table.
        allowed_scopes: options
          .map((option) => option.scope)
          .filter((scope) => form.scopes.includes(scope)),
        redirect_uris: redirectURIsForSubmit(form.redirect_uris),
      };
      const result = await create.mutateAsync({ applicationID, body, reason });
      onDone(`Registered ${result.oauth_client.name} as ${result.oauth_client.client_id}.`);
      return;
    }
    if (editing === null) {
      return;
    }
    const body: OAuthClientEdit = {
      // Echoed from the ROW for the same reason the slug is: there is no box
      // that could move it, and sending it makes an untouched save a 200.
      client_id: editing.client_id,
      name: form.name.trim(),
      // REPLACES the list whole rather than adding to it, so two operators
      // editing one client from two tabs cannot each apply a delta to a list
      // neither last read.
      allowed_scopes: options
        .map((option) => option.scope)
        .filter((scope) => form.scopes.includes(scope)),
    };
    const result = await update.mutateAsync({
      clientID: editing.id,
      body,
      reason,
      applicationID,
    });
    onDone(`Saved ${result.oauth_client.name}.`);
  }

  const idPrefix = editing === null ? "new-client" : `client-${editing.id}`;
  const label = creating ? "Register client" : "Save changes";

  return (
    <div className="esm-subform">
      <h3>{editing === null ? "Register an OAuth client" : `Edit ${editing.name}`}</h3>
      <p className="esm-muted-text">
        {creating
          ? "Every client here is a PUBLIC client with no secret and PKCE required — the schema spells that as a CHECK rather than a default, so it cannot be turned off later either. It is created active, though its application's own status still gates it."
          : "The client id is fixed: every installed copy of the desktop app sends it on every /authorize call and has no way to learn a new one. Callbacks have their own editor, below."}
      </p>
      {placed ? null : (
        <ErrorAlert
          error={error}
          title={creating ? "That client was not registered" : "That change was not saved"}
        />
      )}
      <div className="esm-form-grid">
        <Field
          label="Client id"
          htmlFor={`${idPrefix}-client-id`}
          hint={
            creating
              ? "Unique across the whole platform: /authorize is given a client_id and nothing else to resolve the client by. Immutable once written."
              : "Immutable."
          }
          error={clientIDConflict ?? clientIDError(form.client_id, creating)}
        >
          <Input
            id={`${idPrefix}-client-id`}
            value={form.client_id}
            spellCheck={false}
            disabled={!creating}
            onChange={(event) =>
              setForm((current) => ({ ...current, client_id: event.target.value }))
            }
          />
        </Field>
        <Field
          label="Name"
          htmlFor={`${idPrefix}-name`}
          hint="What operators read, and what a customer sees on the consent screen."
          error={nameError(form.name, "An OAuth client")}
        >
          <Input
            id={`${idPrefix}-name`}
            value={form.name}
            onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
          />
        </Field>
      </div>
      <ScopePicker
        idPrefix={idPrefix}
        options={options}
        chosen={form.scopes}
        onToggle={toggleScope}
        error={scopesError(form.scopes)}
      />
      {creating ? (
        <>
          <h3>Redirect uris</h3>
          <p className="esm-muted-text">
            Compared byte for byte at <code>/authorize</code> and again at <code>/token</code>, so
            a trailing slash or a different port is <em>not registered</em>. A native app should
            register several loopback ports and bind whichever is free.
          </p>
          <RedirectURIRows
            idPrefix={idPrefix}
            uris={form.redirect_uris}
            onChange={(next) => setForm((current) => ({ ...current, redirect_uris: next }))}
            errors={rowErrors}
            listError={issues.list}
            disabled={create.isPending}
          />
        </>
      ) : null}
      <div className="esm-actions">
        <ReasonButton
          variant="primary"
          busy={create.isPending || update.isPending}
          disabled={!ready}
          mutation={{
            title: creating
              ? `Register ${form.name.trim() || "an OAuth client"}`
              : `Save changes to ${form.name.trim() || "this client"}`,
            tone: "primary",
            description: creating ? (
              <p>
                A public client with id <code>{form.client_id.trim() || "…"}</code>, able to ask a
                customer for {form.scopes.length === 1 ? "one scope" : `${form.scopes.length} scopes`}
                . It can authorise from the moment it is written, provided the application is
                enabled.
              </p>
            ) : (
              <p>
                The allow-list is REPLACED by what is ticked above — anything unticked stops being
                askable, and a token already issued against it keeps working until it expires.
              </p>
            ),
            confirmLabel: label,
            run,
          }}
        >
          {label}
        </ReasonButton>
        <Button
          variant="ghost"
          disabled={create.isPending || update.isPending}
          onClick={onClose}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}


// --------------------------------------------------------------------------
// The redirect uri editor
// --------------------------------------------------------------------------

/**
 * Replace one registered client's callback list.
 *
 * Its own component, and its own route on the server, for the reason
 * `OAuthClientForm` states: this is the only edit on a client that can stop a
 * shipped installation from finishing a sign-in, and it earns an audit row
 * carrying the whole list on either side rather than a line in a detail map
 * about a client whose name also changed.
 *
 * The list travels WHOLE, every time. There is deliberately no "add this one"
 * request: an add/remove delta would let two operators in two tabs each apply
 * a change to a list neither of them last read, and the loser's removal would
 * silently come back.
 */
export function RedirectURIEditor({
  applicationID,
  client,
  onClose,
  onDone,
}: {
  applicationID: string;
  client: OAuthClient;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const replace = useReplaceRedirectURIs();
  // Seeded ONCE, from the row as it was when this opened, and never re-seeded:
  // a background refetch landing while an operator is halfway through typing a
  // callback must not take the box back. The caller remounts this per client
  // (key={client.id}), which is what makes "edit a different client" a fresh
  // list rather than the first one's values under a new heading.
  const [uris, setURIs] = useState<string[]>(() => [...client.redirect_uris]);

  const error = replace.error;
  const issues = redirectURIIssues(uris);
  // Trimmed the way the server trims it, because this is both the list the
  // request will carry and the string a refusal quotes back.
  const submitted = redirectURIsForSubmit(uris);
  const refusedURI = offendingRedirectURI(error);
  const refusedRow = refusedURI === undefined ? -1 : submitted.indexOf(refusedURI);
  // The API's sentence, verbatim, against the row the API named; this file's
  // own sentence everywhere else. Both the 400 for a malformed or duplicated
  // entry and the 409 for a callback another client already owns name one.
  const rowErrors = uris.map((_uri, index) =>
    index === refusedRow ? errorMessage(error) : issues.rows[index],
  );
  // Silenced only once the sentence has actually LANDED on a row. A refusal
  // naming a callback no row holds any more must not vanish because it was
  // assumed to be on one.
  const placed = refusedRow >= 0;

  const ready = issues.list === undefined && issues.rows.every((row) => row === undefined);

  // Compared against the row as the page last read it and NOT against what
  // this form was seeded with: a PUT replaces whatever is registered NOW, so
  // that is the list the operator is about to destroy.
  const removing = client.redirect_uris.filter((uri) => !submitted.includes(uri));
  const adding = submitted.filter((uri) => !client.redirect_uris.includes(uri));

  async function run(reason: Reason) {
    const result = await replace.mutateAsync({
      clientID: client.id,
      redirectURIs: submitted,
      reason,
      applicationID,
    });
    const count = result.oauth_client.redirect_uris.length;
    const denied = result.denied_authorization_requests;
    onDone(
      `${result.oauth_client.name} now has ` +
        `${count === 1 ? "one callback" : `${count} callbacks`}.` +
        // Not decoration: each one is an installation that was mid-sign-in
        // against a callback this write just unregistered, and the operator
        // should learn it here rather than from a support ticket.
        (denied === 0
          ? ""
          : ` ${denied === 1 ? "One authorization request was" : `${denied} authorization requests were`}` +
            ` in flight against a callback this removed and ${denied === 1 ? "has" : "have"} been` +
            ` denied (${result.denied_reason}).`),
    );
  }

  return (
    <div className="esm-subform">
      <h3>Callbacks for {client.name}</h3>
      <p className="esm-muted-text">
        Saved as ONE list: what is below <em>replaces</em> what is registered, so a row removed
        here is unregistered and a code already in flight against it is denied. Each entry is
        compared byte for byte at <code>/authorize</code> and again at <code>/token</code>, so a
        trailing slash or a different port is <em>not registered</em>.
      </p>
      {placed ? null : <ErrorAlert error={error} title="That callback list was not saved" />}
      <RedirectURIRows
        idPrefix={`client-${client.id}-callbacks`}
        uris={uris}
        onChange={setURIs}
        errors={rowErrors}
        listError={issues.list}
        disabled={replace.isPending}
      />
      {removing.length > 0 ? (
        // Shown before the dialog, not only inside it: a removal is invisible
        // in a list of text boxes — the row is simply gone — and the operator
        // deleting row 3 by mistake has nothing else to notice it by.
        <Alert
          tone="warning"
          title={
            removing.length === 1
              ? "Saving unregisters one callback"
              : `Saving unregisters ${removing.length} callbacks`
          }
        >
          <ul className="esm-scope-list">
            {removing.map((uri) => (
              <li key={uri}>
                <code>{uri}</code>
              </li>
            ))}
          </ul>
        </Alert>
      ) : null}
      <div className="esm-actions">
        <ReasonButton
          variant="primary"
          busy={replace.isPending}
          disabled={!ready}
          mutation={{
            title: `Replace ${client.name}'s callbacks`,
            // Danger whenever anything is being taken away, primary when the
            // save only adds: unregistering a callback is what strands a
            // fleet, and adding one cannot.
            tone: removing.length > 0 ? "danger" : "primary",
            description: (
              <>
                <p>
                  Client <code>{client.client_id}</code> will be left with exactly{" "}
                  {submitted.length === 1 ? "one callback" : `${submitted.length} callbacks`}.
                </p>
                {removing.length > 0 ? (
                  <p>
                    Unregistering {removing.join(", ")}. Any authorization
                    request already in flight against{" "}
                    {removing.length === 1 ? "it" : "one of them"} is denied.
                  </p>
                ) : null}
                {adding.length > 0 ? <p>Registering {adding.join(", ")}.</p> : null}
                {removing.length === 0 && adding.length === 0 ? (
                  <p>Nothing changes — the list is the one already registered.</p>
                ) : null}
              </>
            ),
            confirmLabel: "Replace callbacks",
            run,
          }}
        >
          Save callbacks
        </ReasonButton>
        <Button variant="ghost" disabled={replace.isPending} onClick={onClose}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
